import GameweekGrid from '../components/GameweekGrid'
import LeaguesList from '../components/LeaguesList'
import PullToRefresh from '../components/PullToRefresh'
import Profile from '../components/Profile'
import PLPage from '../components/PLPage'
import FixturesPage from '../components/FixturesPage'
import type { Tab } from '../components/BottomNav'
import { mockProfile } from '../mockProfile'
import { mockResults } from '../mockResults'
import { getUpcomingGames } from '../teamForm'
import { countSettledFixtures } from '../components/profile/totalPointsMeta'
import {
  buildBestGameweekRecord,
  buildChipTiles,
} from '../components/profile/records'
import type { AppView } from './AppView'

export interface TabPageProps {
  view: AppView
  tab: Tab
  onOpenSettings: () => void
  /** Press a live or settled card on the predict grid to open its fixture. Both this
   *  and the focused id below are owned by AppRoutes, never by this component: the drag-nav shell
   *  renders TabPage twice at once, so state held here would exist in two copies. */
  onOpenFixture?: (gameId: string) => void
  focusedFixtureId?: string | null
  onFixtureFocusConsumed?: () => void
}

/**
 * One bottom-nav destination. Rendered twice at a time by the drag-nav
 * shell — the settled page and, mid-swipe, its neighbour — so this stays a plain switch with no
 * state of its own.
 *
 * `GameweekGrid` keeps its long scalar prop list: with the phase-3 hooks in place the grouped
 * controllers are available here, but passing them through would mean rewriting GameweekGrid's
 * internals, which is out of scope for this ticket (follow-up for the GameweekGrid split).
 */
export default function TabPage({
  view,
  tab,
  onOpenSettings,
  onOpenFixture,
  focusedFixtureId = null,
  onFixtureFocusConsumed,
}: TabPageProps) {
  const {
    gameweek,
    predictions,
    chips,
    quests,
    revealSeen,
    leagues,
    rivals,
    scores,
  } = view
  const { currentGameweek, games, actualResults, isDeadlinePassed } = gameweek
  // Everything below renders results, so it asks the per-fixture question, not the gameweek-level
  // flag. In mock the two are the same value.
  const resultsRevealed = view.resultsVisible

  switch (tab) {
    case 'home':
      return (
        <Profile
          profile={{
            ...mockProfile,
            name: view.identity.displayName,
            favouriteTeam: view.identity.favouriteTeam,
          }}
          leagues={leagues.leagues}
          recentResults={
            (view.identity.favouriteTeam ? mockResults[view.identity.favouriteTeam] : undefined) ?? []
          }
          upcomingFixtures={getUpcomingGames(
            view.identity.favouriteTeam,
            currentGameweek
          )}
          overallScore={scores.overallScore}
          // The delta shows the same running total as the gameweek grid's GW-points
          // display, off the same derivation, so it ticks up with the simulation
          // instead of sitting at 0 until the wholesale reveal.
          currentGameweekPoints={scores.liveGameweekPoints}
          settledFixtures={countSettledFixtures({
            games,
            actualResults,
            resultsRevealed,
            simGameStatuses: view.simulation.gameStatuses,
          })}
          totalFixtures={games.length}
          seasonStats={scores.seasonStats}
          formHistory={scores.formHistory}
          // Only the best gameweek's detail crosses into Profile, not the whole map — a
          // presentational component has no business holding the season's fixtures.
          bestGameweek={buildBestGameweekRecord(
            scores.seasonStats.bestGameweek,
            scores.seasonStats.bestGameweek
              ? view.seasonDetails[scores.seasonStats.bestGameweek.gameweek]
              : null
          )}
          chipTiles={buildChipTiles({
            ledger: {
              doubleUp: chips.doubleUpUsedGameweeks,
              cleanSweep: chips.cleanSweepUsedGameweeks,
              goalfest: chips.goalfestUsedGameweeks,
            },
            seasonHistory: view.seasonHistory,
            settledGameweeks: gameweek.settledGameweeks,
            currentGameweek,
            // No gate on your own profile: you may always see the chip you are playing.
            canRevealCurrentGameweek: true,
          })}
          onOpenGameweek={(gw, walk) =>
            walk ? view.history.selectWalk(gw, walk) : view.history.select(gw)
          }
          currentGameweek={currentGameweek}
          rivals={rivals.rivals}
          incomingRivalRequests={rivals.incomingRequests}
          onViewRivalProfile={view.viewedPlayer.viewPlayer}
          onAcceptRivalRequest={rivals.acceptRequest}
          onDeclineRivalRequest={rivals.declineRequest}
          onOpenSettings={onOpenSettings}
          onChangeFavouriteTeam={view.identity.saveFavouriteTeam}
          avatarPreset={view.identity.avatarPreset}
          onChangeAvatarPreset={view.identity.changeAvatarPreset}
        />
      )

    case 'pl':
      return (
        <PLPage plTable={scores.plTable} />
      )

    case 'leagues':
      return (
        // The manual half of the refresh. The list itself stays a pure component —
        // it neither fetches nor knows it can be pulled.
        <PullToRefresh
          onRefresh={() => view.leaguesRefresh.refresh('pull')}
          enabled={!view.leagueNav.transition.isTransitioning}
        >
          <LeaguesList
            leagues={leagues.leagues}
            onSelectLeague={(leagueId, sourceNameEl) =>
              view.leagueNav.transition.activate(leagueId, sourceNameEl ?? null, {
                className: 'league-transition-clone',
              })
            }
            onPressFeedback={view.leagueNav.transition.pressFeedback}
            transitionActive={view.leagueNav.transition.isTransitioning}
            onCreateLeague={leagues.openCreateLeague}
            onJoinLeague={leagues.openJoinLeague}
            onExploreLeagues={view.exploreLeagues.open}
            seasonStartLabel={scores.seasonStartLabel}
          />
        </PullToRefresh>
      )

    case 'fixtures':
      return (
        <FixturesPage
          currentGameweek={currentGameweek}
          currentGameweekGames={games}
          predictions={predictions.predictions}
          actualResults={actualResults}
          resultsRevealed={resultsRevealed}
          simState={view.simulation.state}
          seasonDetails={view.seasonDetails}
          liveFixtures={view.live.fixtures}
          liveServerTime={view.live.serverTime}
          focusFixtureId={focusedFixtureId}
          onFocusConsumed={onFixtureFocusConsumed}
        />
      )

    case 'predict':
      return (
        <GameweekGrid
          gameweek={currentGameweek}
          games={games}
          predictions={predictions.predictions}
          actualResults={actualResults}
          onSelectGame={predictions.openGame}
          onStartCycle={predictions.startCycle}
          onStartCycleAt={predictions.startCycleAt}
          isCycling={predictions.isCycling}
          history={view.seasonHistory}
          doubleUpUsedGameweeks={chips.doubleUpUsedGameweeks}
          onPlayDoubleUp={chips.playDoubleUp}
          onCancelDoubleUp={chips.cancelDoubleUp}
          cleanSweepUsedGameweeks={chips.cleanSweepUsedGameweeks}
          onPlayCleanSweep={chips.playCleanSweep}
          onCancelCleanSweep={chips.cancelCleanSweep}
          goalfestUsedGameweeks={chips.goalfestUsedGameweeks}
          onPlayGoalfest={chips.playGoalfest}
          onCancelGoalfest={chips.cancelGoalfest}
          goalfestFinalBonusByGameweek={chips.goalfestFinalBonusByGameweek}
          cleanSweepFinalTotalByGameweek={chips.cleanSweepFinalTotalByGameweek}
          deadlinePassed={isDeadlinePassed}
          serverOffsetMs={view.live.serverOffsetMs}
          bankerGameId={predictions.bankerGameId}
          onSimulateDeadline={view.devTools.simulateDeadline}
          resultsRevealed={resultsRevealed}
          onToggleResults={view.devTools.toggleResults}
          onSelectHistoryGameweek={view.history.select}
          onHistoryPressFeedback={view.history.transition.pressFeedback}
          historyTransitionActive={
            view.history.viewingHistoryGameweek !== null ||
            view.history.transition.isTransitioning
          }
          onFinalizeGameweek={view.devTools.finalizeGameweek}
          onSimulateSeason={view.devTools.simulateSeason}
          onDevChangeGameweek={view.devTools.changeGameweek} // dev tool
          onResetAll={view.devTools.resetAll}
          isAdmin={view.identity.canUseDevTools}
          dataMode={view.identity.dataMode}
          // The shared live snapshot — what makes a card go live for every user
          // watching, not just the one whose browser is animating a mock playback.
          liveFixtures={view.live.fixtures}
          liveServerTime={view.live.serverTime}
          simGameStatuses={view.simulation.gameStatuses}
          simLiveResults={view.simulation.liveResults}
          isSimulatingGameweek={view.simulation.isSimulating || view.devTools.isRunningGameweek}
          onSimulateGameweekRealtime={view.devTools.simulateGameweekRealtime}
          revealSeenGameIds={revealSeen.revealSeenGameIds}
          onMarkRevealSeen={revealSeen.markSeen}
          onDevClearRevealSeen={view.devTools.clearRevealSeenForGameweek}
          hasStartedGw={view.hasStartedGw}
          questPicks={quests.questPicks}
          questResults={quests.questResults}
          questsEnabled={view.identity.questsEnabled}
          onPickQuestTeam={quests.pickTeam}
          onPickQuestMatch={quests.pickMatch}
          onSelectQuestSide={quests.selectSide}
          onOpenFixture={onOpenFixture}
          gameweekStats={view.gameweekStats}
          myUserId={view.identity.myUserId}
          firstPlayableGameweek={view.identity.firstPlayableGameweek}
          // Their calls for this gameweek are the reason to press it, so it opens over the
          // gameweek rather than pushing their whole profile.
          onOpenTopScorer={(userId, displayName) =>
            view.topScorer.show(userId, displayName, view.gameweek.currentGameweek)
          }
        />
      )
  }
}
